import React, { useMemo } from 'react';
import './GeoChecklistPage.scss';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { useAccount } from '../../shared/contexts/account/AccountContexts';
import { getProjectsFromAccount } from '../../shared/helpers/project/ProjectHelper';
import { GEO_CHECKLIST, PROJECTS } from '../../shared/routes/path';
import Title from '../../shared/components/title/Title';
import useIsMobile from '../../shared/hooks/global/UseIsMobile';
import MobilePageHeader from '../../shared/components/common/mobile-menu/MobilePageHeader';
import { buildChecklistSections, CHECKLIST_TAB_DEFS } from './data/checklistData';
import type { ChecklistTab } from './data/checklistData';
import { useGetGeoChecklist } from '../../shared/api/hooks/geo-checklist/useGetGeoChecklist';
import { useGetGeoChecklistCatalog } from '../../shared/api/hooks/geo-checklist/useGetGeoChecklistCatalog';
import { useToggleGeoChecklistTask } from '../../shared/api/hooks/geo-checklist/useToggleGeoChecklistTask';
import { useResetGeoChecklist } from '../../shared/api/hooks/geo-checklist/useResetGeoChecklist';
import ChecklistIntro from './components/ChecklistIntro';
import ChecklistProgressBar from './components/ChecklistProgressBar';
import ChecklistSection from './components/ChecklistSection';
import ChecklistPdfExport from './components/ChecklistPdfExport';
import GeoPlaybook from './components/playbook/GeoPlaybook';

const GeoChecklistPage: React.FC = () => {
  const { projectId, tab: tabSlug } = useParams<{ projectId: string; tab: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  const { projectResponse, accountResponse } = useAccount();

  const projectIdNumber = projectId ? Number(projectId) : null;

  const projects = useMemo(
    () => getProjectsFromAccount(accountResponse, projectResponse ?? []),
    [accountResponse, projectResponse]
  );

  const currentProject = useMemo(
    () => projects.find((p) => p.id === projectIdNumber) ?? null,
    [projects, projectIdNumber]
  );

  const visibleTabs = useMemo(() => CHECKLIST_TAB_DEFS.filter((tab) => !tab.hidden), []);

  const activeTabDef = useMemo(
    () => visibleTabs.find((tab) => tab.slug === tabSlug) ?? visibleTabs[0],
    [tabSlug, visibleTabs]
  );

  // Sections come from the catalog API (canon in llm-common); null until the catalog arrives.
  const { data: catalog } = useGetGeoChecklistCatalog();
  const activeTab: ChecklistTab | null = useMemo(() => {
    if (!activeTabDef || !catalog) return null;
    const { sectionOrder, ...tab } = activeTabDef;
    return { ...tab, sections: buildChecklistSections(catalog.types[tab.type], sectionOrder) };
  }, [activeTabDef, catalog]);

  const { data: checklistData } = useGetGeoChecklist(projectIdNumber);
  const toggleTask = useToggleGeoChecklistTask();
  const resetChecklist = useResetGeoChecklist();

  const checkedTasks: Record<string, string> = useMemo(() => {
    if (!checklistData || !activeTab) return {};
    return checklistData[activeTab.type] ?? {};
  }, [checklistData, activeTab]);

  const globalProgress = useMemo(() => {
    if (!activeTab) return { checked: 0, total: 0, percent: 0 };
    const allTaskIds = activeTab.sections.flatMap((s) => s.tasks.map((t) => t.id));
    const total = allTaskIds.length;
    const checked = allTaskIds.filter((id) => id in checkedTasks).length;
    const percent = total > 0 ? Math.round((checked / total) * 100) : 0;
    return { checked, total, percent };
  }, [activeTab, checkedTasks]);

  const handleToggle = (taskId: string) => {
    if (!projectIdNumber || !activeTabDef) return;
    toggleTask.mutate({
      projectId: projectIdNumber,
      checklistType: activeTabDef.type,
      taskId,
    });
  };

const confirmReset = () => {
    confirmDialog({
      header: t('geoChecklist.resetConfirmTitle'),
      message: t('geoChecklist.resetConfirmMessage'),
      acceptLabel: t('geoChecklist.resetConfirmYes'),
      rejectLabel: t('geoChecklist.resetConfirmNo'),
      accept: () => {
        if (!projectIdNumber || !activeTabDef) return;
        resetChecklist.mutate({
          projectId: projectIdNumber,
          checklistType: activeTabDef.type,
        });
      },
    });
  };

  const handleProjectChange = (e: { value: typeof currentProject }) => {
    if (!e.value) return;
    navigate(`${GEO_CHECKLIST}/${e.value.id}/${tabSlug}`);
  };

  // The header renders immediately; only the checklist body waits for the catalog fetch below.
  if (!accountResponse || !activeTabDef) {
    return null;
  }

  if (!projectIdNumber || (!currentProject && projects.length > 0)) {
    return <Navigate to={PROJECTS} replace />;
  }

  return (
    <div className="geo-checklist-page page-content">
      <ConfirmDialog className="dialog-box" />
      <div className="page-box">
        {/* Telefon: w górnym pasku tytuł i akcje (niebieskie przyciski z ikoną), wybór projektu pod paskiem
            na całą szerokość. */}
        <MobilePageHeader
          title={<span>{t('geoChecklist.title')}</span>}
          actions={
            <>
              {activeTab && (
                <ChecklistPdfExport
                  projectName={currentProject?.projectLabel ?? ''}
                  activeTab={activeTab}
                  checkedTasks={checkedTasks}
                  buttonClassName="btn btn--sm mobile-header__add"
                />
              )}
              <button
                type="button"
                className="btn btn--sm mobile-header__add"
                onClick={confirmReset}
                aria-label={t('geoChecklist.resetChecklist')}
              >
                <span className="ico--refresh ico--xs" />
              </button>
            </>
          }
        />
        {isMobile ? (
          <div className="mobile-page-filters">
            <Dropdown
              value={currentProject}
              onChange={handleProjectChange}
              options={projects}
              optionLabel="projectLabel"
              className="dropdown"
            />
          </div>
        ) : (
        <div className="page-box__filters" data-observe-sticky>
          <Title
            title={t('geoChecklist.title')}
            childrenOnMain={<div className="title-box__subtitle">{t('geoChecklist.subtitle')}</div>}
            children={
              <div className="filters">
                <div className="filters-panel">
                  <Dropdown
                    value={currentProject}
                    onChange={handleProjectChange}
                    options={projects}
                    optionLabel="projectLabel"
                    className="dropdown"
                  />
                  {activeTab && (
                    <ChecklistPdfExport
                      projectName={currentProject?.projectLabel ?? ''}
                      activeTab={activeTab}
                      checkedTasks={checkedTasks}
                    />
                  )}
                  <Button className="btn btn--sm btn--secondary" onClick={confirmReset}>
                    <span className="ico--refresh ico--xs" />
                    {t('geoChecklist.resetChecklist')}
                  </Button>
                </div>
              </div>
            }
          />
        </div>
        )}

        <div className="page-box__content">
          {activeTab &&
            (activeTab.design === 'playbook' ? (
              <GeoPlaybook
                tab={activeTab}
                checkedTasks={checkedTasks}
                onToggle={handleToggle}
                progress={globalProgress}
              />
            ) : (
              <>
                <ChecklistIntro tab={activeTab} />
                <ChecklistProgressBar
                  checked={globalProgress.checked}
                  total={globalProgress.total}
                  percent={globalProgress.percent}
                />
                {activeTab.sections.map((section) => (
                  <ChecklistSection
                    key={section.id}
                    section={section}
                    checkedTasks={checkedTasks}
                    onToggle={handleToggle}
                  />
                ))}
              </>
            ))}
        </div>
      </div>
    </div>
  );
};

export { GeoChecklistPage };
export default GeoChecklistPage;
